import React, { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, Search } from "lucide-react";
import { getCategories, getSubCategories } from "../../api/taxonomy";
import { useToast } from "../../context/ToastContext";

const EventTypeSearch = ({
  value, // either ObjectId or { id, name, type }
  onChange, // function(selectedItem)
  placeholder = "Select Event Type",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { showError } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchEventTypes = async () => {
      setLoading(true);
      try {
        const catRes = await getCategories("common");
        if (!isMounted) return;
        
        const combined = [];
        if (catRes?.data) {
          catRes.data.forEach(c => {
            combined.push({
              id: c._id,
              name: c.name,
              type: c.isSubcategory ? "SubCategory" : "Category",
              isSubcategory: c.isSubcategory,
              categories: c.parentIds || []
            });
          });
        }
        setItems(combined);
      } catch (err) {
        console.error("Failed to fetch event types", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchEventTypes();
    return () => { isMounted = false; };
  }, []);

  const selectedItem = useMemo(() => {
    if (!value) return null;
    const valId = typeof value === "object" ? value.id || value._id || value : value;
    return items.find(i => i.id === valId);
  }, [value, items]);

  const selectedCategoryItem = useMemo(() => {
    if (!selectedItem) return null;
    if (selectedItem.type === "Category") return selectedItem;
    // Find parent category in items
    const parentId = Array.isArray(selectedItem.categories) 
      ? selectedItem.categories[0] 
      : selectedItem.categories;
    if (parentId) {
      const parentIdStr = typeof parentId === "object" && parentId !== null ? parentId._id || parentId.id : parentId;
      return items.find(i => i.id === parentIdStr);
    }
    return null;
  }, [selectedItem, items]);

  const subcategoriesOfSelected = useMemo(() => {
    if (!selectedCategoryItem) return [];
    return items.filter(item => 
      item.type === "SubCategory" && 
      item.categories && 
      (Array.isArray(item.categories) ? item.categories : [item.categories]).some(c => {
        const cId = typeof c === "object" && c !== null ? c._id || c.id || c : c;
        return cId === selectedCategoryItem.id;
      })
    );
  }, [selectedCategoryItem, items]);

  // Update input when value changes externally
  useEffect(() => {
    if (value && typeof value === "object") {
      setSearchTerm(value.name || "");
    } else if (typeof value === "string") {
      const matched = items.find(i => i.id === value);
      if (matched) {
        setSearchTerm(matched.name);
      } else {
        setSearchTerm(value.replace("suggested_", ""));
      }
    } else {
      setSearchTerm("");
    }
  }, [value, items]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        // Reset search term to current value if discarded
        if (value && typeof value === "object") {
          setSearchTerm(value.name || "");
        } else if (typeof value === "string") {
          const matched = items.find(i => i.id === value);
          if (matched) {
            setSearchTerm(matched.name);
          } else {
            setSearchTerm(value.replace("suggested_", ""));
          }
        } else {
          setSearchTerm("");
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value, items]);

  const isSearchActive = useMemo(() => {
    if (!searchTerm) return false;
    if (selectedItem && searchTerm.toLowerCase() === selectedItem.name.toLowerCase()) return false;
    return true;
  }, [searchTerm, selectedItem]);

  const displayItems = useMemo(() => {
    if (isSearchActive) {
      const lower = searchTerm.trim().toLowerCase();
      const queryWords = lower.split(/\s+/);
      return items.filter(item => {
        const nameWords = (item.name || "").toLowerCase().split(/\s+/);
        return queryWords.every(qWord => 
          nameWords.some(nWord => nWord.startsWith(qWord))
        );
      });
    }
    return items;
  }, [items, searchTerm, isSearchActive]);

  const hasExactMatch = useMemo(() => {
    if (!searchTerm) return true;
    const lower = searchTerm.trim().toLowerCase();
    return items.some(item => (item.name || "").toLowerCase() === lower);
  }, [items, searchTerm]);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div className="relative">
        <input
          type="text"
          className={`w-full px-3 h-[36px] border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF4C18] bg-white pr-8 text-xs sm:text-sm text-gray-900 placeholder:text-gray-400 ${isOpen ? "border-[#FF4C18]" : ""} ${disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`}
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => {
            if (disabled) return;
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (!disabled) setIsOpen(true);
          }}
          disabled={disabled}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
          <Search size={16} className={`transition-colors ${isOpen ? "text-[#FF4C18]" : ""}`} />
        </div>
      </div>

      {isOpen && !disabled && (
        <div 
          className="absolute top-full left-0 w-full mt-1 bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.1)] py-2 z-50 max-h-60 overflow-y-auto no-scrollbar"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {loading && (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">Loading...</div>
          )}
          {!loading && displayItems.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 py-4 text-center px-4">
              <p className="text-sm text-gray-500">
                No results for <span className="font-semibold text-[#FF4C18]">"{searchTerm}"</span>
              </p>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  const name = searchTerm.trim();
                  if (!name) return;
                  if (name.replace(/\s+/g, ' ').split(' ').length > 3) {
                    showError("Suggestion can be at most 3 words.");
                    return;
                  }
                  if (name.length > 35) {
                    showError("Suggestion can be at most 35 characters.");
                    return;
                  }
                  onChange({
                    id: "suggested_" + name,
                    name: name,
                    type: "Suggestion",
                    isSuggestion: true
                  });
                  setIsOpen(false);
                }}
                className="px-6 py-1.5 bg-[#FF4C18] text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors"
                style={{ fontFamily: "CabinetGrotesk-Bold" }}
              >
                Create
              </button>
            </div>
          )}
          {!loading && displayItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(item);
                setIsOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-gray-50 text-gray-700"
            >
              {item.name}
            </button>
          ))}
          {!loading && !hasExactMatch && searchTerm.trim() && displayItems.length > 0 && (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                const name = searchTerm.trim();
                if (!name) return;
                if (name.replace(/\s+/g, ' ').split(' ').length > 3) {
                  showError("Suggestion can be at most 3 words.");
                  return;
                }
                if (name.length > 35) {
                  showError("Suggestion can be at most 35 characters.");
                  return;
                }
                onChange({
                  id: "suggested_" + name,
                  name: name,
                  type: "Suggestion",
                  isSuggestion: true
                });
                setIsOpen(false);
              }}
              className="w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-orange-50 text-[#FF4C18] font-bold border-t border-dashed border-gray-100 flex items-center justify-between"
            >
              <span>+ Create "{searchTerm.trim()}"</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default EventTypeSearch;
